import { Button } from 'components/button/Button';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from 'reducers/modal';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';
import {
  selectHideScenarioModal,
  setHideScenarioModal,
} from 'reducers/assessment';
import { Tooltip } from 'components/helpers/Tooltip';
import ClevryRobot from 'assets/apply.clevry/Img_Robot.png';
import InfoIcon from 'assets/apply.clevry/Icon_Password_Info.png';
import { selectAccessibilityData } from 'reducers/accessibility';
import { selectAssessmentName, selectSkipContent } from 'reducers/assessment';
import ScenarioImg1 from 'assets/apply.clevry/BlendedTestScenarioInmages/Image1.png';
import ScenarioImg2 from 'assets/apply.clevry/BlendedTestScenarioInmages/Image2.png';
import ScenarioImg3 from 'assets/apply.clevry/BlendedTestScenarioInmages/Image3.png';
import ScenarioImg4 from 'assets/apply.clevry/BlendedTestScenarioInmages/Image4.png';
import ScenarioImg4Test from 'assets/apply.clevry/BlendedTestScenarioInmages/Image4_test.png';

type ModalProps = {
  data: {
    title: string;
    scenarioText: string;
    scenarioImage?: string | null;
    prongCode: string;
    testType: string;
    hasSeenAllScenarios: boolean;
  };
};

export const AssessmentScenarioModal: React.FC<ModalProps> = ({ data }) => {
  const scenarioModal = useRef<HTMLInputElement>(null);
  const [generalError, setGeneralError] = useState<string>();

  const dispatch = useDispatch();
  const accessibilityData = useSelector(selectAccessibilityData);

  const { t } = useTranslation('Assessment+Scenario+Modal', { i18n });

  const hideScenarioModal = useSelector(selectHideScenarioModal);
  const assessmentName = useSelector(selectAssessmentName);
  const skipContentSet = useSelector(selectSkipContent);

  const triggerNextFocus = () => {
    const assessmentContainer: any = document.getElementsByClassName("AssessmentOuter")[0];
    if(assessmentContainer){
      const allFocusEleArr: any = assessmentContainer.querySelectorAll('a, button, input, textarea, select, details, [tabindex]');
      allFocusEleArr[0].focus();
    }
  };   

  const handleCloseModal = () => {
    if(skipContentSet){
      triggerNextFocus();
    } else {
      const skipContentBtn = document.getElementsByClassName("SkipContentBtn")[0] as HTMLElement;
      const accessibilityBtn = document.getElementsByClassName("AccessibilityBtn")[0] as HTMLElement;
      if(skipContentBtn){
        skipContentBtn.focus();
      } else if(accessibilityBtn){
        accessibilityBtn.focus();
      }
    }

    dispatch(closeModal());
  };

  const handleDontShowAgain = () => {
    dispatch(
      setHideScenarioModal({
        ...hideScenarioModal,
        all: true,
        // [data.testType]: true,
      })
    );
    handleCloseModal();
  };

  const currentWebskin = window.sessionStorage.getItem('webskin')
    ? window.sessionStorage.getItem('webskin')
    : 'defaultTheme';  

  useEffect(() => {
    scenarioModal.current?.focus();
  }, [scenarioModal]);  

  useEffect(() => {
    const eleToScroll = document.getElementsByClassName("AssessmentModal")[0].getElementsByClassName("Content")[0];
    eleToScroll.scrollTo(0, 0);
  }, []);  

  const modalStyle = {
    '--modalTitleColor': '#036C8F',
    '--footerBackgroundColor': '#f5f5f5',
    '--footerBorderColor': '#f5f5f5',
    '--primaryButtonBackgroundColor': '#36705B',
    '--tertiaryButtonBackgroundColor': '#d5d5d5',
  } as React.CSSProperties;

  return (
    <div
      className={`AssessmentModal Scenario ${accessibilityData.classStr}` +
        `${(assessmentName ? (' ' + assessmentName) : '')}` + 
        `${currentWebskin !== 'defaultTheme' ? (' ' + currentWebskin) : ''}`
      }
      style={modalStyle}
    >
      <div className="Content">
        <div className="Image">
          <img src={data.scenarioImage ?? ClevryRobot} alt={t('SSCandidate_Scenario_Modal_Alt_Popup_Description')} />
        </div>

        <div
          className="SpeechBubble"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(data.scenarioText),
          }}
          ref={scenarioModal}
          tabIndex={0}
        />
      </div>
      <div className="ModalFooter BtmBtns">
        {generalError && <div className="GeneralError">{generalError}</div>}
        {data.hasSeenAllScenarios && (
          <Button
            className="Rounded"
            priority="tertiary"
            type="button"
            onClick={handleDontShowAgain}
          >
            {t('SSCandidate_Scenario_Modal_Dont_Show_Again')}
            <Tooltip
              text={t('SSCandidate_Scenario_Modal_Dont_Show_Again_Tooltip')}
              image={InfoIcon}
            />
          </Button>
        )}

        <Button
          className="Discard Rounded"
          priority="primary"
          type="submit"
          onClick={handleCloseModal}
          addTabIndex={0}
        >
          {t('SSCandidate_Scenario_Modal_Understand')}
        </Button>
      </div>
    </div>
  );
};
